import { Flex } from '@servicetitan/anvil2';
import { formatAddress } from '@servicetitan/cp-ui';
import { DateTimeUtils } from '@servicetitan/datetime-utils';
import { Card, Stack, Headline, BodyText, Icon } from '@servicetitan/design-system';
import { useDependencies } from '@servicetitan/react-ioc';
import { FC, Fragment, useMemo } from 'react';
import { DashboardServiceAgreementsData } from '../../../api/portal-dashboard.api';
import { CountDisplay } from './count-display';
import { CoveredJobsFooter } from './covered-jobs-footer';
import { LocationBodyText } from './location-body-text';
import * as Styles from './service-agreement-dashboard.module.less';
import { ServiceAgreementCoveredLocationsDrawerStore } from './service-agreement-drawer/stores/service-agreement-covered-locations-drawer.store';
import { ServiceAgreementStatusTag } from './service-agreement-status-tag';

interface ReadyToSignServiceAgreementContent {
    serviceAgreement: DashboardServiceAgreementsData;
    mobile?: boolean;
    viewServiceAgreementButton?: JSX.Element;
}

export const ReadyToSignServiceAgreementContent: FC<ReadyToSignServiceAgreementContent> = ({
    mobile,
    serviceAgreement,
    viewServiceAgreementButton,
}) => {
    const [dateTimeUtils] = useDependencies(DateTimeUtils);
    const [{ open }] = useDependencies(ServiceAgreementCoveredLocationsDrawerStore);
    const monthsOfCoverageCount = useMemo(() => {
        const { endDate, startDate } = serviceAgreement || {};
        if (!startDate || !endDate) {
            return 0;
        }
        const start = new Date(startDate);
        const end = new Date(endDate);
        return (
            (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()) + 1
        );
    }, [serviceAgreement]);

    const displayMonthsOfCoverage = serviceAgreement.startDate && serviceAgreement.endDate;

    return (
        <Fragment>
            <Card.Section className="p-t-2 p-b-2">
                <Flex direction="row" justifyContent="space-between" alignItems="center">
                    <Flex direction={mobile ? 'column' : 'row'} columnGap="2" rowGap="1">
                        <Headline size="medium" className="m-b-0 t-truncate">
                            {serviceAgreement.name}
                        </Headline>
                        <Flex gap={1}>
                            <ServiceAgreementStatusTag status={serviceAgreement.status} />
                        </Flex>
                    </Flex>
                    {!mobile && <Stack.Item shrink={0}>{viewServiceAgreementButton}</Stack.Item>}
                </Flex>
            </Card.Section>
            <Card.Section className="p-t-2 p-b-2">
                <Flex direction={mobile ? 'column' : 'row'} gap={3} justifyContent="space-between">
                    <Flex
                        direction="column"
                        style={
                            mobile
                                ? {
                                      flex: '1 0 33.33%',
                                  }
                                : {
                                      flex: '0 0 33.33%',
                                  }
                        }
                    >
                        <CountDisplay
                            count={serviceAgreement.coveredLocations.length}
                            name="Covered Locations"
                            mobile={mobile}
                            footerContent={
                                serviceAgreement.coveredLocations.length === 1 ? (
                                    <LocationBodyText
                                        location={serviceAgreement.coveredLocations[0].zip}
                                    >
                                        {formatAddress(serviceAgreement.coveredLocations[0])}
                                    </LocationBodyText>
                                ) : (
                                    <a
                                        onClick={() => open(serviceAgreement.coveredLocations)}
                                        className={Styles.viewAllLocationsButton}
                                        role="button"
                                    >
                                        View all Locations
                                    </a>
                                )
                            }
                            includeDivider={false}
                        />
                    </Flex>
                    <Flex
                        direction="column"
                        style={
                            mobile
                                ? {
                                      flex: '1 0 33.33%',
                                  }
                                : {
                                      flex: '0 0 33.33%',
                                  }
                        }
                    >
                        <CountDisplay
                            count={serviceAgreement.coveredJobs.length}
                            name="Covered Jobs"
                            includeDivider={!mobile}
                            mobile={mobile}
                            footerContent={
                                <CoveredJobsFooter coveredJobs={serviceAgreement.coveredJobs} />
                            }
                        />
                    </Flex>
                    <Flex
                        direction="column"
                        style={
                            mobile
                                ? {
                                      flex: '0 1 33.33%',
                                  }
                                : {
                                      flex: '0 0 33.33%',
                                  }
                        }
                    >
                        {displayMonthsOfCoverage && (
                            <CountDisplay
                                count={monthsOfCoverageCount}
                                name="Months Coverage"
                                mobile={mobile}
                                includeDivider={!mobile}
                                footerContent={
                                    <Flex direction="column">
                                        <BodyText size="small" subdued>
                                            <Icon
                                                name="event"
                                                size={16}
                                                className="m-r-half m-b-half"
                                            />
                                            {serviceAgreement.startDate &&
                                                `${dateTimeUtils.formatShortMonth(serviceAgreement.startDate)} ${serviceAgreement.startDate.getDate()}, ${serviceAgreement.startDate.getFullYear()}`}
                                            <Icon
                                                name="arrow_forward"
                                                size={16}
                                                className="m-r-half m-b-half"
                                            />
                                            {serviceAgreement.endDate &&
                                                `${dateTimeUtils.formatShortMonth(serviceAgreement.endDate)} ${serviceAgreement.endDate.getDate()}, ${serviceAgreement.endDate.getFullYear()}`}
                                        </BodyText>
                                    </Flex>
                                }
                            />
                        )}
                    </Flex>
                </Flex>
            </Card.Section>
            {mobile && (
                <Card.Section className="p-t-2 p-b-2">
                    <Flex justifyContent="center">{viewServiceAgreementButton}</Flex>
                </Card.Section>
            )}
        </Fragment>
    );
};
